import React, { useEffect, useState } from 'react';
import { Trash2, ChevronDown, ChevronUp, ArrowRightLeft, Group } from 'lucide-react';
import { formatCNPJ } from '../../../../utils/masks';
import PercentageInput from '../../../../components/ui/inputs/PercentageInput';
import useHighlightOnAdd from '../../../../components/hooks/useHighlightOnAdd';

const ComplementRow = ({
  comp,
  compIdx,
  gridColumns,
  parentOriginalIndex,
  highlightId,
  onRemoveComplementaryAsset,
}) => {
  const shouldHighlight = comp._highlightId != null && comp._highlightId === highlightId;
  const { ref, isHighlighted } = useHighlightOnAdd(shouldHighlight);

  return (
    <div
      ref={ref}
      className={`grid items-center gap-4 px-3 py-4 text-blueDarker rounded transition-colors group hover:bg-white/40 ${
        isHighlighted ? 'bg-yellowMedium' : ''
      }`}
      style={{ gridTemplateColumns: gridColumns, transitionDuration: isHighlighted ? '0ms' : '2000ms' }}
    >
      <span className="truncate font-semibold">{comp.displayName}</span>
      <span>{comp.cnpj ? formatCNPJ(comp.cnpj) : '--'}</span>
      <span>{comp.cge || '--'}</span>

      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          tabIndex={-1}
          className="bg-redSoft hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-3 pb-3 text-xs border-r-2 border-b-2 border-redDeep"
          onClick={() => onRemoveComplementaryAsset(parentOriginalIndex, compIdx)}
          title="Remover Ativo Complementar"
        >
          <Trash2 size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

const AssetRow = ({
  c,
  idx,
  gridColumns,
  highlightId,
  onChangeAllocation,
  onRemoveAsset,
  onReplaceAsset,
  onRemoveComplementaryAsset,
  onAddComplementaryAsset,
  onRequestOpen,
}) => {
  const visibleComplements = (c.complementaryAssets || []).filter((comp) => !comp._destroy);
  const hasComplements = visibleComplements.length > 0;
  const hasHighlightedComplement = visibleComplements.some((comp) => comp._highlightId === highlightId);

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (hasHighlightedComplement) setIsExpanded(true);
  }, [hasHighlightedComplement]);

  const shouldHighlightSelf = c._highlightId != null && c._highlightId === highlightId;
  const { ref, isHighlighted } = useHighlightOnAdd(shouldHighlightSelf);

  const isBlueRow = idx % 2 !== 1;
  const rowBackground = isHighlighted ? 'bg-yellowExtraLight' : isBlueRow ? 'bg-blueLight' : 'bg-transparent';

  return (
    <div
      ref={ref}
      className={`${rowBackground} rounded-xl transition-colors`}
      style={{ transitionDuration: isHighlighted ? '0ms' : '3000ms' }}
    >
      {/* LINHA PRINCIPAL DO ATIVO */}
      <div className="grid items-center gap-4 px-3 py-3" style={{ gridTemplateColumns: gridColumns }}>
        {/* Nome do Ativo (Clicável se tiver complementos) */}
        <div
          className={`min-w-0 flex items-center gap-2 font-semibold text-blueDarker ${
            hasComplements ? 'cursor-pointer hover:opacity-80' : ''
          }`}
          title={c.assetDisplayName || c.assetName || c.displayName || c.name}
          onClick={() => hasComplements && setIsExpanded(!isExpanded)}
        >
          <span className="min-w-0 truncate">
            {c.assetDisplayName || c.assetName || c.displayName || c.name || '--'}
          </span>
          {hasComplements && (
            <span className="bg-grayExtraLight4 rounded-full" style={{ padding: '3px' }}>
              {isExpanded ? (
                <ChevronUp size={12} className={`text-blueMedium transition-transform duration-300`} strokeWidth={2} />
              ) : (
                <ChevronDown
                  size={12}
                  className={`text-blueMedium transition-transform duration-300`}
                  strokeWidth={2}
                />
              )}
            </span>
          )}
        </div>

        {/* CNPJ */}
        {c.assetType !== 'Equity' && (
          <div className="truncate text-blueDarker" title={formatCNPJ(c.assetCnpj)}>
            {formatCNPJ(c.assetCnpj)}
          </div>
        )}

        {/* tSector*/}
        {c.assetType === 'Equity' && (
          <div className="truncate text-blueDarker" title={c.assetTSector}>
            {c.assetTSector}
          </div>
        )}

        {/* CGE */}
        {c.assetType !== 'Equity' && (
          <div className="truncate text-blueDarker" title={c.assetCge || 'Sem CGE'}>
            {c.assetCge || '--'}
          </div>
        )}

        {/* ticker */}
        {c.assetType === 'Equity' && (
          <div className="truncate text-blueDarker" title={c.assetTicker}>
            {c.assetTicker}
          </div>
        )}

        {/* Botões de Ação */}
        <div className="flex justify-end gap-2">
          <PercentageInput
            name={`allocation-${c.originalIndex}`}
            value={c.allocationPercentage}
            onChange={(e) => onChangeAllocation(c.originalIndex, e.target.value)}
            onFocus={onRequestOpen}
            py="py-2"
          />
          <button
            tabIndex={-1}
            onClick={() => onReplaceAsset(c, c.originalIndex)}
            className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
          >
            <ArrowRightLeft size={18} className="inline-block" strokeWidth={1.5} />
          </button>
          <button
            tabIndex={-1}
            className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
            onClick={() => onAddComplementaryAsset(c, c.originalIndex)}
            title="Adicionar Ativo Complementar"
          >
            <Group size={18} className="inline-block" strokeWidth={1.5} />
          </button>
          <button
            tabIndex={-1}
            className="bg-redSoft hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-2 pb-2 text-xs border-r-2 border-b-2 border-redDeep"
            onClick={() => onRemoveAsset(c.originalIndex)}
          >
            <Trash2 size={16} className="inline-block" strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* SUB-LISTA DE ATIVOS COMPLEMENTARES */}
      {hasComplements && (
        <div
          className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
          style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <hr className="border-grayExtraLight4" />

            {(c.complementaryAssets || []).map((comp, compIdx) => {
              if (comp._destroy) return null;

              return (
                <ComplementRow
                  key={comp.id || `comp-${compIdx}`}
                  comp={comp}
                  compIdx={compIdx}
                  gridColumns={gridColumns}
                  parentOriginalIndex={c.originalIndex}
                  highlightId={highlightId}
                  onRemoveComplementaryAsset={onRemoveComplementaryAsset}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// --- ACCORDION DAS CATEGORIAS (Ações, FIIs, etc) ---
const AssetAccordion = ({
  title,
  assets,
  highlightId,
  onRemoveAsset,
  onChangeAllocation,
  defaultOpen = true,
  onReplaceAsset,
  onRemoveComplementaryAsset,
  onAddComplementaryAsset,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const hasHighlightedAsset =
    highlightId != null &&
    assets.some(
      (asset) =>
        asset._highlightId === highlightId ||
        (asset.complementaryAssets || []).some((comp) => comp._highlightId === highlightId)
    );

  useEffect(() => {
    if (hasHighlightedAsset) setIsOpen(true);
  }, [hasHighlightedAsset]);

  if (assets.length === 0) return null;

  const gridColumns = '2fr 1.2fr 140px 300px';
  const sumPercentages = assets.reduce((sum, asset) => sum + parseFloat(asset.allocationPercentage || 0), 0);

  const isEquities = assets.every((asset) => asset.assetType === 'Equity');

  return (
    <div className="flex flex-col gap-3">
      <div
        className="grid items-center text-base bg-tealMedium text-white font-semibold p-3 rounded-xl cursor-pointer select-none transition-colors hover:bg-tealDark shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
        style={{ gridTemplateColumns: gridColumns }}
      >
        <span className="flex items-center gap-2">
          {title}
          {/* <span className="bg-white text-tealMedium px-2 py-0.5 rounded-full text-xs font-bold">{assets.length}</span> */}
        </span>
        <span></span>
        <span></span>
        <span className="flex justify-between items-center">
          <span className="bg-white text-tealMedium px-2 py-0.5 rounded-full text-sm font-bold">
            {sumPercentages.toFixed(1).replace('.', ',')}%
          </span>
          {<>{isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</>}
        </span>
      </div>

      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="pt-1 pb-2">
            <div className="px-2">
              <div
                className="grid items-center gap-4 text-blueDarker text-base font-semibold px-1"
                style={{ gridTemplateColumns: gridColumns }}
              >
                <div className="truncate">Ativo</div>
                <div className="truncate">{isEquities ? 'Setor' : 'CNPJ'}</div>
                <div className="truncate">{isEquities ? 'Ticker' : 'CGE'}</div>
                <div></div>
              </div>
            </div>

            <div className="space-y-1 mt-2">
              {assets.map((c, idx) => (
                <AssetRow
                  key={c.id || `new-${c.originalIndex}`}
                  c={c}
                  idx={idx}
                  gridColumns={gridColumns}
                  highlightId={highlightId}
                  onChangeAllocation={onChangeAllocation}
                  onRemoveAsset={onRemoveAsset}
                  onReplaceAsset={onReplaceAsset}
                  onRemoveComplementaryAsset={onRemoveComplementaryAsset}
                  onAddComplementaryAsset={onAddComplementaryAsset}
                  onRequestOpen={() => setIsOpen(true)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- RENDA FIXA ---
const FixedIncomeAccordion = ({ percentage, onChange, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const val = parseFloat(percentage || 0);

  return (
    <div className="flex flex-col gap-3">
      <div
        className="grid items-center text-base bg-tealMedium text-white font-semibold p-3 rounded-xl cursor-pointer select-none transition-colors hover:bg-tealDark shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
        style={{ gridTemplateColumns: '2fr 1.2fr 140px 300px' }}
      >
        <span className="flex items-center gap-2">Renda Fixa</span>
        <span></span>
        <span></span>
        <span className="flex justify-between items-center gap-4">
          <span className="bg-white text-tealMedium px-2 py-0.5 rounded-full text-sm font-bold">
            {val.toFixed(1).replace('.', ',')}%
          </span>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </div>

      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="pt-1 pb-2">
            <div className="bg-blueLight rounded-xl transition-colors">
              <div className="flex items-center justify-between px-3 py-3">
                <div className="font-semibold text-blueDarker">Investimentos em Renda Fixa</div>
                <div className="w-32">
                  <PercentageInput
                    name="fixedIncomePercentage"
                    value={percentage}
                    onChange={(e) => onChange(e.target.value)}
                    inputClassName="py-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getAssetSortKey = (asset) =>
  (asset.assetDisplayName || asset.assetName || asset.displayName || asset.name || '').toLowerCase();

const byDisplayName = (a, b) => getAssetSortKey(a).localeCompare(getAssetSortKey(b), 'pt-BR');

// --- COMPONENTE PRINCIPAL ---
const WalletAssets = ({
  assetsAttributes,
  highlightId,
  onRemoveAsset,
  onChangeAllocation,
  onReplaceAsset,
  onRemoveComplementaryAsset,
  onAddComplementaryAsset,
  fixedIncomePercentage,
  onChangeFixedIncome,
  walletType,
}) => {
  const visibleAssets = assetsAttributes
    .map((asset, index) => ({ ...asset, originalIndex: index }))
    .filter((asset) => !asset._destroy);

  const investmentFunds = visibleAssets.filter((asset) => asset.assetType === 'InvestmentFund').sort(byDisplayName);
  const pensionFunds = visibleAssets.filter((asset) => asset.assetType === 'PensionFund').sort(byDisplayName);
  const fiiAssets = visibleAssets
    .filter((asset) => asset.assetKind === 'fii' || asset.assetType === 'fii')
    .sort(byDisplayName);
  const stockAssets = visibleAssets
    .filter((asset) => asset.assetKind === 'stock' || asset.assetType === 'stock')
    .sort(byDisplayName);
  const otherAssets = visibleAssets
    .filter(
      (asset) =>
        !['InvestmentFund', 'PensionFund', 'fii', 'stock'].includes(asset.assetType) &&
        !['fii', 'stock'].includes(asset.assetKind)
    )
    .sort(byDisplayName);

  const commonProps = {
    highlightId,
    onRemoveAsset,
    onChangeAllocation,
    onReplaceAsset,
    onRemoveComplementaryAsset,
    onAddComplementaryAsset,
  };

  return (
    <div className="mt-4 flex flex-col gap-5 pb-6 w-full">
      <AssetAccordion title="Fundos de Investimento" assets={investmentFunds} {...commonProps} />
      <AssetAccordion title="Fundos de Previdência" assets={pensionFunds} {...commonProps} />
      <AssetAccordion title="FIIs" assets={fiiAssets} {...commonProps} />
      <AssetAccordion title="Ações" assets={stockAssets} {...commonProps} />
      <AssetAccordion title="Outros Ativos" assets={otherAssets} {...commonProps} />
      {walletType === 'investment' && (
        <FixedIncomeAccordion percentage={fixedIncomePercentage} onChange={onChangeFixedIncome} />
      )}
    </div>
  );
};

export default WalletAssets;
